import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
import { ActivityResponseObject } from '~/schemas/conversation';

enableMapSet();

interface ActivitiesState {
  items: Map<string, ActivityResponseObject>;
  cursorsByAgent: Map<string, string | null>;
  loadedAgents: Set<string>;
}

const initialState: ActivitiesState = {
  items: new Map(),
  cursorsByAgent: new Map(),
  loadedAgents: new Set(),
};

const activitiesSlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {
    addActivities: (
      state,
      action: PayloadAction<{
        activities: ActivityResponseObject[];
        agentId: string;
        nextCursor?: string | null;
      }>,
    ) => {
      const { activities, agentId, nextCursor } = action.payload;

      // Update activities map
      activities.forEach((activity) => {
        state.items.set(activity._id, activity);
      });

      // Update cursor for the agent
      state.cursorsByAgent.set(
        agentId,
        nextCursor || state.cursorsByAgent.get(agentId) || null,
      );

      // Mark agent as loaded
      state.loadedAgents.add(agentId);
    },
    updateActivity: (state, action: PayloadAction<ActivityResponseObject>) => {
      const activity = action.payload;
      state.items.set(activity._id, activity);
    },
    clearAgentData: (state, action: PayloadAction<string>) => {
      const agentId = action.payload;
      state.cursorsByAgent.delete(agentId);
      state.loadedAgents.delete(agentId);
      // Note: We don't clear activities as they might be shared between agents
    },
  },
});

export const { addActivities, updateActivity, clearAgentData } =
  activitiesSlice.actions;

// Define RootState type
export interface RootState {
  activities: ActivitiesState;
}

// Selectors
const selectActivitiesMap = (state: RootState) => state.activities.items;
const selectAgentId = (_state: RootState, agentId: string) => agentId;
const EMPTY_ACTIVITIES: ActivityResponseObject[] = [];

export const selectActivitiesByAgent = createSelector(
  [selectActivitiesMap, selectAgentId],
  (activitiesMap, agentId): ActivityResponseObject[] => {
    if (!agentId) {
      return EMPTY_ACTIVITIES;
    }
    const filtered = Array.from(activitiesMap.values()).filter(
      (activity) => activity.agent_id === agentId,
    );

    if (filtered.length === 0) {
      return EMPTY_ACTIVITIES;
    }

    return filtered.sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  },
);

export const selectNextCursor = (state: RootState, agentId: string) => {
  return state.activities.cursorsByAgent.get(agentId) ?? null;
};

export const selectIsAgentLoaded = (state: RootState, agentId: string) => {
  return state.activities.loadedAgents.has(agentId);
};

export default activitiesSlice.reducer;
